import React, { useState, useEffect } from 'react';
import { useLead } from '../../contexts/LeadContext';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiX, FiFacebook, FiInstagram, FiLinkedin, FiEdit, FiPlus, FiGlobe } = FiIcons;

const LeadForm = ({ onClose, lead = null }) => {
  const { addLead, updateLead } = useLead();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: lead?.name || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    website: lead?.website || '',
    company_name: lead?.company_name || '',
    company_address: lead?.company_address || '',
    company_city: lead?.company_city || '',
    company_postal_code: lead?.company_postal_code || '',
    facebook: lead?.facebook || '',
    instagram: lead?.instagram || '',
    linkedin: lead?.linkedin || '',
    service_requested: lead?.service_requested || '',
    lead_source: lead?.lead_source || '',
    notes: lead?.notes || '',
    ai_reference_notes: lead?.ai_reference_notes || '',
    status: lead?.status || 'New'
  });
  
  // For managing options
  const [customOptions, setCustomOptions] = useState({
    service: '',
    source: '',
    status: ''
  });
  
  const [serviceOptions, setServiceOptions] = useState([
    'Residential Wiring',
    'Commercial Electrical',
    'Panel Upgrades',
    'Lighting Installation',
    'Outlet Installation',
    'Electrical Repairs',
    'Emergency Service',
    'Electrical Inspection',
    'Generator Installation',
    'Smart Home Setup',
    'Other'
  ]);

  const [leadSourceOptions, setLeadSourceOptions] = useState([
    'Referral',
    'Website',
    'Google Ads',
    'Facebook',
    'Instagram',
    'LinkedIn',
    'Yellow Pages',
    'Walk-in',
    'Cold Call',
    'Other'
  ]);

  const [statusOptions, setStatusOptions] = useState(['New', 'Contacted', 'Scheduled', 'Completed']);

  // Fetch existing options from the database
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch all unique values from leads
        const { data, error } = await supabase
          .from('leads')
          .select('service_requested, lead_source, status');
          
        if (error) throw error;
        
        if (data) {
          const services = [...new Set(data.map(d => d.service_requested).filter(Boolean))];
          const sources = [...new Set(data.map(d => d.lead_source).filter(Boolean))];
          const statuses = [...new Set(data.map(d => d.status).filter(Boolean))];
          
          // Update options with existing values
          setServiceOptions(prev => [...new Set([...prev, ...services])]);
          setLeadSourceOptions(prev => [...new Set([...prev, ...sources])]);
          setStatusOptions(prev => [...new Set([...prev, ...statuses])]);
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    
    fetchOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleCustomOptionChange = (e) => {
    const { name, value } = e.target;
    setCustomOptions(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const addCustomOption = (type) => {
    const value = customOptions[type];
    if (!value) return;
    
    switch (type) {
      case 'service':
        if (!serviceOptions.includes(value)) {
          setServiceOptions(prev => [...prev, value]);
          setFormData(prev => ({ ...prev, service_requested: value }));
        }
        break;
      case 'source':
        if (!leadSourceOptions.includes(value)) {
          setLeadSourceOptions(prev => [...prev, value]);
          setFormData(prev => ({ ...prev, lead_source: value }));
        }
        break;
      case 'status':
        if (!statusOptions.includes(value)) {
          setStatusOptions(prev => [...prev, value]);
          setFormData(prev => ({ ...prev, status: value }));
        }
        break;
      default:
        break;
    }
    
    // Clear the custom input
    setCustomOptions(prev => ({
      ...prev,
      [type]: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (lead) {
        console.log("Updating lead with form data:", formData);
        result = await updateLead(lead.id, formData);
      } else {
        console.log("Adding new lead with form data:", formData);
        result = await addLead(formData);
      }
      
      console.log("Operation result:", result);
      
      if (result.success) {
        onClose();
      } else {
        console.error("Operation failed:", result.error);
        alert(`Failed to ${lead ? 'update' : 'add'} lead: ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      alert(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to generate proper social media URLs
  const generateSocialMediaUrl = (platform, value) => {
    if (!value) return '';
    
    // If it already contains http, return as is
    if (value.includes('http')) {
      return value;
    }
    
    // Generate appropriate URL based on platform
    switch (platform) {
      case 'facebook':
        return `https://www.facebook.com/${value}`;
      case 'instagram':
        return `https://www.instagram.com/${value}`;
      case 'linkedin':
        return `https://www.linkedin.com/in/${value}`;
      default:
        return `https://${value}`;
    }
  };
  
  // Function to open social media links
  const openSocialMedia = (platform, url) => {
    if (!url) return;
    
    const fullUrl = generateSocialMediaUrl(platform, url);
    window.open(fullUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {lead ? 'Edit Lead' : 'Add New Lead'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Requested
                  </label>
                  <div className="flex gap-2">
                    <input
                      list="service-options"
                      name="service_requested"
                      value={formData.service_requested}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      placeholder="Select or type a service"
                    />
                    <datalist id="service-options">
                      <option value="">Select a service</option>
                      {serviceOptions.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </datalist>
                  </div>
                  <div className="flex mt-1 items-center">
                    <input
                      type="text"
                      name="service"
                      value={customOptions.service}
                      onChange={handleCustomOptionChange}
                      placeholder="Add new service option"
                      className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                    <button
                      type="button"
                      onClick={() => addCustomOption('service')}
                      className="ml-2 p-1 bg-gray-100 hover:bg-gray-200 rounded-full"
                    >
                      <SafeIcon icon={FiPlus} className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Source
                  </label>
                  <div className="flex gap-2">
                    <input
                      list="source-options"
                      name="lead_source"
                      value={formData.lead_source}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      placeholder="Select or type a source"
                    />
                    <datalist id="source-options">
                      <option value="">Select lead source</option>
                      {leadSourceOptions.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </datalist>
                  </div>
                  <div className="flex mt-1 items-center">
                    <input
                      type="text"
                      name="source"
                      value={customOptions.source}
                      onChange={handleCustomOptionChange}
                      placeholder="Add new source option"
                      className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                    <button
                      type="button"
                      onClick={() => addCustomOption('source')}
                      className="ml-2 p-1 bg-gray-100 hover:bg-gray-200 rounded-full"
                    >
                      <SafeIcon icon={FiPlus} className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </div>
                
                {lead && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div className="flex gap-2">
                      <input
                        list="status-options"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                        placeholder="Select or type a status"
                      />
                      <datalist id="status-options">
                        {statusOptions.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </datalist>
                    </div>
                    <div className="flex mt-1 items-center">
                      <input
                        type="text"
                        name="status"
                        value={customOptions.status}
                        onChange={handleCustomOptionChange}
                        placeholder="Add new status option"
                        className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      />
                      <button
                        type="button"
                        onClick={() => addCustomOption('status')}
                        className="ml-2 p-1 bg-gray-100 hover:bg-gray-200 rounded-full"
                      >
                        <SafeIcon icon={FiPlus} className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Address
                  </label>
                  <input
                    type="text"
                    name="company_address"
                    value={formData.company_address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="company_city"
                      value={formData.company_city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="company_postal_code"
                      value={formData.company_postal_code}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                    />
                  </div>
                </div>
                
                {/* Social Media with Visit Links */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-700">Social Media Profiles</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Facebook
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleChange}
                        placeholder="URL or username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      />
                      <button
                        type="button"
                        onClick={() => openSocialMedia('facebook', formData.facebook)}
                        disabled={!formData.facebook}
                        className="px-3 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <SafeIcon icon={FiFacebook} className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instagram
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        placeholder="URL or username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      />
                      <button
                        type="button"
                        onClick={() => openSocialMedia('instagram', formData.instagram)}
                        disabled={!formData.instagram}
                        className="px-3 py-2 bg-pink-600 text-white rounded-r-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <SafeIcon icon={FiInstagram} className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleChange}
                        placeholder="URL or username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                      />
                      <button
                        type="button"
                        onClick={() => openSocialMedia('linkedin', formData.linkedin)}
                        disabled={!formData.linkedin}
                        className="px-3 py-2 bg-blue-800 text-white rounded-r-lg hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <SafeIcon icon={FiLinkedin} className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Private Notes
              </label>
              <textarea
                name="notes"
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                placeholder="Additional notes about this lead (not shared with AI)..."
              />
            </div>

            {/* AI Reference Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AI Reference Notes
              </label>
              <textarea
                name="ai_reference_notes"
                rows={3}
                value={formData.ai_reference_notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                placeholder="Notes to be included in AI prompts for this lead..."
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-2 bg-electric-600 text-white rounded-lg hover:bg-electric-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon icon={FiSave} className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save Lead'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeadForm;